import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import { ModalComponent } from '../modal/modal.component';
import { ToastService } from '../../../services/toast.service';
import { PermissionService } from '../../../services/permission.service';
import { ContactoApiService } from '../../../../pages/contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../../pages/empresas/services/empresa-api.service';
import { CrmImportExportApiService } from '../../../services/crm-import-export-api.service';
import {
  ImportMode,
  ImportResultResponse,
  MAX_IMPORT_FILE_BYTES
} from '../../../models/import-export.model';

type Tab = 'contacts' | 'companies' | 'combined';

interface TabConfig {
  key: Tab;
  label: string;
  description: string;
  /**
   * Módulo RBAC contra el que se gatean las acciones de esta pestaña.
   * - contacts  -> 'contacts'
   * - companies -> 'companies'
   * - combined  -> 'contacts' (el backend gatea la importación/exportación combinada sobre contactos)
   */
  permModule: string;
  exportFilename: string;
  templateFilename: string;
  export: () => Observable<Blob>;
  template: () => Observable<Blob>;
  import: (file: File, mode: ImportMode) => Observable<ImportResultResponse>;
}

@Component({
  selector: 'app-import-export-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './import-export-modal.component.html'
})
export class ImportExportModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() initialTab: Tab = 'contacts';
  @Output() close = new EventEmitter<void>();

  activeTab: Tab = 'contacts';
  mode: ImportMode = 'SKIP';
  selectedFile: File | null = null;
  fileError = '';

  exporting = false;
  downloadingTemplate = false;
  importing = false;

  result: ImportResultResponse | null = null;
  showAllErrors = false;

  readonly maxFileBytes = MAX_IMPORT_FILE_BYTES;

  private readonly tabs: TabConfig[];

  constructor(
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private crmApi: CrmImportExportApiService,
    private toast: ToastService,
    private permissions: PermissionService
  ) {
    this.tabs = [
      {
        key: 'contacts',
        label: 'Contactos',
        description: 'Exporta o importa contactos con sus atributos personalizados (EAV).',
        permModule: 'contacts',
        exportFilename: 'contacts.csv',
        templateFilename: 'contacts_template.csv',
        export: () => this.contactoApi.exportContactsCsv(),
        template: () => this.contactoApi.downloadContactsImportTemplate(),
        import: (f, m) => this.contactoApi.importContactsCsv(f, m)
      },
      {
        key: 'companies',
        label: 'Empresas',
        description: 'Exporta o importa empresas. Dedup por nombre comercial.',
        permModule: 'companies',
        exportFilename: 'companies.csv',
        templateFilename: 'companies_template.csv',
        export: () => this.empresaApi.exportCompaniesCsv(),
        template: () => this.empresaApi.downloadCompaniesImportTemplate(),
        import: (f, m) => this.empresaApi.importCompaniesCsv(f, m)
      },
      {
        key: 'combined',
        label: 'Contactos + Empresas',
        description: 'Una fila = 1 contacto + 1 empresa + la asociación entre ambos. Sin atributos EAV.',
        permModule: 'contacts',
        exportFilename: 'crm_contacts_companies.csv',
        templateFilename: 'crm_contacts_companies_template.csv',
        export: () => this.crmApi.exportCombinedCsv(),
        template: () => this.crmApi.downloadCombinedTemplate(),
        import: (f, m) => this.crmApi.importCombinedCsv(f, m)
      }
    ];
  }

  /** Exportar CSV de la pestaña activa, gateado por el permiso del módulo correspondiente. */
  get canExport(): boolean {
    return this.permissions.canExport(this.currentTab.permModule);
  }

  /** Descargar plantilla + Importar de la pestaña activa, gateado por el permiso del módulo. */
  get canImport(): boolean {
    return this.permissions.canImport(this.currentTab.permModule);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.activeTab = this.initialTab ?? 'contacts';
      this.resetForm();
    }
  }

  get tabsList(): TabConfig[] {
    return this.tabs;
  }

  get currentTab(): TabConfig {
    return this.tabs.find(t => t.key === this.activeTab) ?? this.tabs[0];
  }

  get visibleErrors() {
    if (!this.result) return [];
    return this.showAllErrors ? this.result.errors : this.result.errors.slice(0, 10);
  }

  selectTab(tab: Tab): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.resetForm();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    this.fileError = '';
    this.result = null;

    if (!file) {
      this.selectedFile = null;
      return;
    }

    const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
    if (!isCsv) {
      this.fileError = 'El archivo debe tener extensión .csv';
      this.selectedFile = null;
      input.value = '';
      return;
    }

    if (file.size > this.maxFileBytes) {
      this.fileError = `El archivo supera los 5 MB (tamaño actual: ${this.formatBytes(file.size)}).`;
      this.selectedFile = null;
      input.value = '';
      return;
    }

    this.selectedFile = file;
  }

  exportar(): void {
    if (this.exporting) return;
    this.exporting = true;
    const tab = this.currentTab;
    tab.export()
      .pipe(finalize(() => (this.exporting = false)))
      .subscribe({
        next: blob => this.downloadBlob(blob, tab.exportFilename),
        error: err => this.handleError(err, 'No se pudo exportar el CSV.')
      });
  }

  descargarPlantilla(): void {
    if (this.downloadingTemplate) return;
    this.downloadingTemplate = true;
    const tab = this.currentTab;
    tab.template()
      .pipe(finalize(() => (this.downloadingTemplate = false)))
      .subscribe({
        next: blob => this.downloadBlob(blob, tab.templateFilename),
        error: err => this.handleError(err, 'No se pudo descargar la plantilla.')
      });
  }

  importar(): void {
    if (this.importing || !this.selectedFile) return;
    this.importing = true;
    this.result = null;
    this.showAllErrors = false;
    const tab = this.currentTab;
    tab.import(this.selectedFile, this.mode)
      .pipe(finalize(() => (this.importing = false)))
      .subscribe({
        next: res => {
          this.result = res;
          if (res.failed > 0) {
            this.toast.warning({
              title: 'Importación con errores',
              message: `${res.created} creados, ${res.updated} actualizados, ${res.failed} fallaron.`
            });
          } else {
            this.toast.success({
              title: 'Importación completa',
              message: `${res.created} creados, ${res.updated} actualizados, ${res.skipped} omitidos.`
            });
          }
        },
        error: err => this.handleError(err, 'No se pudo importar el CSV.')
      });
  }

  cerrar(): void {
    if (this.exporting || this.downloadingTemplate || this.importing) return;
    this.close.emit();
  }

  resetForm(): void {
    this.selectedFile = null;
    this.fileError = '';
    this.mode = 'SKIP';
    this.result = null;
    this.showAllErrors = false;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private handleError(err: unknown, fallback: string): void {
    let message = fallback;
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (typeof body === 'string' && body.trim()) {
        message = body;
      } else if (body && typeof body === 'object') {
        const maybe = (body as { message?: string; error?: string }).message
          ?? (body as { message?: string; error?: string }).error;
        if (maybe) message = maybe;
      }
      if (err.status === 403) {
        message = 'No tienes permiso para realizar esta operación.';
      } else if (err.status === 401) {
        message = 'Tu sesión expiró. Volvé a iniciar sesión.';
      }
    }
    this.toast.error({ title: 'Error', message });
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}
